import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { HTMLSelect as Select } from '@/components/ui/Select'
import { Input } from '@/components/ui/Input'
import { X, CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import type { Employee } from '@/types/workforce'

interface UpdateEmployeeStatusModalProps {
  employee: Employee
  onConfirm: (status: 'ACTIVE' | 'INACTIVE' | 'TERMINATED', endDate?: string, reason?: string) => Promise<void>
  onCancel: () => void
}

export function UpdateEmployeeStatusModal({
  employee,
  onConfirm,
  onCancel
}: UpdateEmployeeStatusModalProps) {
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE' | 'TERMINATED'>(
    employee.employment?.status || 'ACTIVE'
  )
  const [endDate, setEndDate] = useState('')
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await onConfirm(
        status,
        status === 'TERMINATED' ? endDate : undefined,
        status === 'TERMINATED' ? reason : undefined
      )
    } catch (error) {
      console.error('Error updating status:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getStatusIcon = (statusValue: string) => {
    switch (statusValue) {
      case 'ACTIVE':
        return <CheckCircle className="w-5 h-5 text-success" />
      case 'INACTIVE':
        return <XCircle className="w-5 h-5 text-brand-gray" />
      case 'TERMINATED':
        return <AlertTriangle className="w-5 h-5 text-error" />
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-brand-dark border border-brand-border rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-brand-border">
          <h2 className="font-display text-xl font-semibold text-white">
            Atualizar Status do Colaborador
          </h2>
          <button
            onClick={onCancel}
            className="text-brand-gray hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Colaborador
              </label>
              <p className="text-white font-medium">
                {employee.personalInfo?.name || employee.email}
              </p>
              <p className="text-sm text-brand-gray">{employee.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Status Atual
              </label>
              <div className="flex items-center gap-2">
                {getStatusIcon(employee.employment?.status || 'ACTIVE')}
                <span className="text-white capitalize">
                  {employee.employment?.status?.toLowerCase() || 'active'}
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-brand-gray mb-2">
                Novo Status
              </label>
              <Select
                id="status"
                value={status}
                onValueChange={(value) => setStatus(value as any)}
                required
              >
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
                <option value="TERMINATED">Demitido</option>
              </Select>
            </div>

            {status === 'TERMINATED' && (
              <>
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-brand-gray mb-2">
                    Data de Término
                  </label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-brand-gray mb-2">
                    Motivo (Opcional)
                  </label>
                  <Input
                    id="reason"
                    type="text"
                    placeholder="Motivo da demissão..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex gap-3 p-6 border-t border-brand-border">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Atualizando...' : 'Atualizar Status'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
